import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { 
    Box, Typography, Container, Paper, 
    Accordion, AccordionSummary, AccordionDetails,
    CircularProgress, Alert 
} from '@mui/material';
import { FaChevronDown } from 'react-icons/fa';
import TournamentSheet from '../components/TournamentSheet';
import { useAuth } from '../provider/auth';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import ConfirmModal from '../components/ConfirmModal';

const Tournaments = () => {
    const { user } = useAuth();
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [expanded, setExpanded] = useState(false);

    const fetchTournaments = () => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/tournaments`, { withCredentials: true })
            .then(res => {
                setTournaments(res.data);
                // Auto-expand if only one tournament
                if (res.data.length === 1) {
                    setExpanded(res.data[0]._id);
                }
            })
            .catch(err => {
                console.error("Failed to fetch tournaments", err);
                toast.error("Turniere konnten nicht geladen werden.");
            })
            .finally(() => setLoading(false));
    };

    const fetchFamilyMembers = () => {
        axios.get(`${API_BASE_URL}/api/my-family-members`, { withCredentials: true })
            .then(res => {
               setFamilyMembers(res.data);
            })
            .catch(err => console.error("Failed to fetch family members", err));
    };

    useEffect(() => {
        fetchTournaments();
        if (user) {
            fetchFamilyMembers();
        }
    }, [user]);

    const handleRegister = (tournamentId, data) => {
        axios.post(`${API_BASE_URL}/api/tournaments/${tournamentId}/register`, data, { withCredentials: true })
            .then(res => {
                toast.success("Erfolgreich angemeldet!");
                // Update local state
                setTournaments(prev => prev.map(t => t._id === res.data._id ? res.data : t));
            })
            .catch(err => {
                console.error(err);
                toast.error("Anmeldung fehlgeschlagen: " + (err.response?.data?.message || err.message));
            });
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [participantToRemove, setParticipantToRemove] = useState(null);

    const handleRemove = (tournamentId, participantId) => {
        setParticipantToRemove({ tournamentId, participantId });
        setConfirmOpen(true);
    };

    const executeRemove = () => {
        if (!participantToRemove) return;
        const { tournamentId, participantId } = participantToRemove;
        
        axios.delete(`${API_BASE_URL}/api/tournaments/${tournamentId}/participants/${participantId}`, { withCredentials: true })
            .then(res => {
                toast.info("Ausgetragen.");
                setTournaments(prev => prev.map(t => t._id === res.data._id ? res.data : t));
                setConfirmOpen(false);
                setParticipantToRemove(null);
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim Austragen: " + (err.response?.data?.message || err.message));
            });
    };

    const handleAccordionChange = (tournamentId) => (event, isExpanded) => {
        setExpanded(isExpanded ? tournamentId : false);
    };

    if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
                        Turniere
                    </Typography>
                </Box>
                
                {tournaments.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>Aktuell sind keine Turniere geplant.</Alert>
                ) : (
                    <Box>
                        {tournaments.map(t => (
                            <Accordion 
                                key={t._id} 
                                expanded={expanded === t._id}
                                onChange={handleAccordionChange(t._id)}
                                sx={{ mb: 1 }}
                            >
                                <AccordionSummary
                                    expandIcon={<FaChevronDown />}
                                    sx={{ 
                                        '&:hover': { bgcolor: 'action.hover' },
                                        minHeight: 64
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="h6" fontWeight="bold">
                                            {t.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {dayjs(t.date).format('DD. MMMM YYYY')}
                                            {t.deadline && ` • Anmeldeschluss: ${dayjs(t.deadline).format('DD.MM.YYYY')}`}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <TournamentSheet 
                                        tournament={t} 
                                        onRegister={handleRegister} 
                                        onRemove={handleRemove}
                                        currentUserId={user?._id}
                                        isAdmin={user?.role === 2 || user?.isAdmin}
                                        familyMembers={familyMembers}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                )}
            </Paper>
            
            <ConfirmModal 
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={executeRemove}
                title="Austragen bestätigen"
                message="Möchtest du diesen Teilnehmer wirklich austragen?"
                confirmText="Austragen"
                type="delete"
            />
        </Container>
    );
};

export default Tournaments;
